import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';

import { ArticleSummary } from '../../../shared/types';
import { getLinkPrefix } from '../../../utils/list-utils';
import FetchMorePaginator from '../../ui/FetchMorePaginator';
import { PinnableArticlePreviewCard } from './ArticlePreviewCard';
import { getArticleLink } from '../../../utils/link.utils';
import { getPublishedData } from '../../ui/PublishedBy';
import { useArticleList } from '../../hooks/use-article-list';
import { NoArticlesPlaceholder } from './NoArticlesPlaceholder';
import { useOnDataLoaded } from '../../hooks/use-on-data-loaded';

export interface BrowseArticleListProps {
    title: string;
    categoryId?: string;
    premiumChannelId?: string;
    showPlacement: boolean;
    showFilter?: boolean;
    displayInChartSection?: boolean;
    pinnedFirst?: boolean;
    onDataLoaded?: () => void;
}

enum FilterType {
    All,
    Premium,
    Free,
}

export const BrowseArticleList = (props: BrowseArticleListProps) => {
    const batchSize = 10;
    const {
        categoryId,
        premiumChannelId,
        showFilter = false,
        displayInChartSection = false,
        pinnedFirst = false,
    } = props;
    const queryArticles = useArticleList({
        categoryId,
        premiumChannelId,
        batchSize,
        displayInChartSection,
        pinnedFirst,
    });
    useOnDataLoaded(queryArticles.loading, props.onDataLoaded);
    const [filterType, setFilterType] = useState<FilterType>(FilterType.All);

    useEffect(() => {
        let freeOnly = undefined;
        let premiumOnly = undefined;

        if (filterType === FilterType.Premium) {
            premiumOnly = true;
        }
        if (filterType === FilterType.Free) {
            freeOnly = true;
        }

        queryArticles.refetch({
            batchSize,
            categoryId,
            premiumChannelId,
            freeOnly,
            premiumOnly,
            displayInChartSection,
            pinnedFirst,
        });
    }, [filterType]);

    if (queryArticles.loading || !queryArticles.articles) return <></>;

    const articles = queryArticles.articles;

    if (queryArticles.totalCount === 0 && props.showPlacement && !showFilter)
        return <NoArticlesPlaceholder isChannel={!!props.premiumChannelId} />;

    const showingCaption = `Showing ${articles.length} of ${queryArticles.totalCount} articles`;

    const filterColor = (selectedFilterType: FilterType) =>
        selectedFilterType === filterType ? 'primary' : 'default';
    const useSeparatedArticleRows = !!premiumChannelId;

    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <Typography variant="h5">{props.title}</Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        mt: 1,
                    }}
                >
                    {showingCaption}
                </Typography>
            </Grid>
            {showFilter && (
                <Grid size={12}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                            label="All articles"
                            color={filterColor(FilterType.All)}
                            variant={
                                filterType === FilterType.All
                                    ? 'solid'
                                    : 'outlined'
                            }
                            onClick={() => setFilterType(FilterType.All)}
                        />
                        <Chip
                            label="Premium"
                            color={filterColor(FilterType.Premium)}
                            variant={
                                filterType === FilterType.Premium
                                    ? 'solid'
                                    : 'outlined'
                            }
                            onClick={() => setFilterType(FilterType.Premium)}
                        />
                        <Chip
                            label="Free"
                            color={filterColor(FilterType.Free)}
                            variant={
                                filterType === FilterType.Free
                                    ? 'solid'
                                    : 'outlined'
                            }
                            onClick={() => setFilterType(FilterType.Free)}
                        />
                    </Box>
                </Grid>
            )}
            {showFilter &&
            queryArticles.totalCount === 0 &&
            props.showPlacement ? (
                <Grid size={12}>
                    <Paper sx={{ py: 6 }}>
                        <NoArticlesPlaceholder
                            isChannel={!!props.premiumChannelId}
                        />
                    </Paper>
                </Grid>
            ) : (
                <></>
            )}
            <Grid size={12}>
                <Grid container>
                    {articles.map((article, index) => (
                        <Grid
                            key={article._id}
                            sx={{ pb: article.isPinned ? 2 : 0 }}
                            size={{
                                xs: 12,
                                md: 12,
                            }}
                        >
                            <PinnableArticlePreviewCard
                                source="BrowseCategory"
                                channel={article.premiumChannelDetails}
                                title={article.title}
                                summary={article.summary}
                                articleLink={getArticleLink(article._id)}
                                publishedData={getPublishedData(
                                    article as ArticleSummary,
                                    getLinkPrefix(),
                                    false,
                                    false
                                )}
                                isPinned={article.isPinned}
                                displayMode={
                                    useSeparatedArticleRows
                                        ? 'separated'
                                        : 'card'
                                }
                                showSeparator={index < articles.length - 1}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Grid>
            {!(queryArticles.totalCount === 0 && props.showPlacement) &&
                showFilter && (
                    <Grid
                        container
                        size={{
                            xs: 12,
                            lg: 12,
                        }}
                        sx={{
                            justifyContent: 'space-around',
                        }}
                    >
                        <FetchMorePaginator
                            hasNextPage={!!queryArticles.hasNextPage}
                            label="articles"
                            showingCurrent={queryArticles?.articles?.length}
                            totalCount={queryArticles.totalCount || 0}
                            fetchMore={queryArticles.fetchMore}
                            displayShowMoreButton={true}
                        />
                    </Grid>
                )}
        </Grid>
    );
};

export default BrowseArticleList;
